import { Injectable } from '@angular/core';
import { environment } from '@cpd-app/environment';
import posthog from 'posthog-js';

@Injectable({ providedIn: 'root' })
export class PosthogService {
  private initialized = false;

  /**
   * Initialize PostHog with configuration from environment
   */
  init(): void {
    if (this.initialized) {
      return;
    }

    if (!environment.posthogKey || !environment.posthogHost) {
      console.warn('PostHog: API key or host not configured');
      return;
    }

    try {
      posthog.init(environment.posthogKey, {
        api_host: environment.posthogHost,
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        // VIA-3: session replay stays off. CPD screens show military service
        // records, JST/ACE transcript detail, and free-text the learner types,
        // and replays would land in PostHog US Cloud, a third-party processor
        // outside any CUI boundary. Product analytics (pageviews, autocapture,
        // custom events) are unaffected by this flag.
        //
        // Belt and braces: the PostHog project's own "Record user sessions"
        // toggle should stay off as well. This flag makes the client refuse to
        // record regardless of what the project setting says.
        disable_session_recording: true,
        persistence: 'localStorage+cookie',
        loaded: (posthog) => {
          if (environment.envName === 'LOCAL') {
            posthog.debug();
          }
        }
      });

      this.initialized = true;
      console.log('PostHog initialized successfully');
    } catch (error) {
      console.error('PostHog initialization failed:', error);
    }
  }

  /**
   * Identify a user with their unique ID and properties
   * @param userId - Unique user identifier
   * @param properties - User properties (email, name, etc.)
   */
  identify(userId: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.identify(userId, properties);
    } catch (error) {
      console.error('PostHog identify failed:', error);
    }
  }

  /**
   * Track a custom event
   * @param event - Name of the event
   * @param properties - Event properties
   */
  capture(event: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.capture(event, properties);
    } catch (error) {
      console.error('PostHog capture failed:', error);
    }
  }

  /**
   * Set user properties
   * @param properties - Properties to set
   */
  setPersonProperties(properties: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error('PostHog set person properties failed:', error);
    }
  }

  /**
   * Reset the current user (e.g., on logout)
   */
  reset(): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      console.error('PostHog reset failed:', error);
    }
  }

  /**
   * Register super properties (sent with every event)
   * @param properties - Properties to register
   */
  register(properties: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.register(properties);
    } catch (error) {
      console.error('PostHog register failed:', error);
    }
  }

  /**
   * Get feature flag value
   * @param flagKey - Feature flag key
   */
  isFeatureEnabled(flagKey: string): boolean {
    if (!this.initialized) {
      return false;
    }

    try {
      return posthog.isFeatureEnabled(flagKey) || false;
    } catch (error) {
      console.error('PostHog isFeatureEnabled failed:', error);
      return false;
    }
  }

  /**
   * Get feature flag payload
   * @param flagKey - Feature flag key
   */
  getFeatureFlag(flagKey: string): string | boolean | undefined {
    if (!this.initialized) {
      return undefined;
    }

    try {
      return posthog.getFeatureFlag(flagKey);
    } catch (error) {
      console.error('PostHog getFeatureFlag failed:', error);
      return undefined;
    }
  }

  /**
   * Track page view manually
   * @param properties - Page view properties
   */
  capturePageview(properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.capture('$pageview', properties);
    } catch (error) {
      console.error('PostHog capture pageview failed:', error);
    }
  }

  /**
   * Opt out of tracking
   */
  optOutCapturing(): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.opt_out_capturing();
    } catch (error) {
      console.error('PostHog opt out capturing failed:', error);
    }
  }

  /**
   * Opt in to tracking
   */
  optInCapturing(): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.opt_in_capturing();
    } catch (error) {
      console.error('PostHog opt in capturing failed:', error);
    }
  }
}
